import React, { useState, useEffect } from 'react';
import Logo from './Logo';
import DetailPromo from './DetailPromo';
import apiClient from '../utils/apiClient';
import { API_ENDPOINTS } from '../config/api';

const PromoBerita = ({ onBack, activeBottomTab, setActiveBottomTab, initialSelectedPromo, fromMemberArea }) => {
  const [showDetailPromo, setShowDetailPromo] = useState(false);
  const [selectedPromo, setSelectedPromo] = useState(initialSelectedPromo);
  const [promoData, setPromoData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch artikel dari API
  useEffect(() => {
    const fetchArticles = async () => {
      try {
        setLoading(true);
        setError(null);

        // Build query parameters
        const params = new URLSearchParams({
          limit: '50',
          offset: '0'
        });

        const response = await apiClient.get(`${API_ENDPOINTS.ARTIKEL.PUBLIC_LIST}?${params.toString()}`);

        // Handle response structure
        let articlesFromApi = [];
        if (response.data && response.data.artikels && Array.isArray(response.data.artikels)) {
          articlesFromApi = response.data.artikels;
        } else if (response.data && Array.isArray(response.data)) {
          articlesFromApi = response.data;
        } else if (Array.isArray(response)) {
          articlesFromApi = response;
        }

        // Filter hanya artikel yang published
        const publishedArticles = articlesFromApi.filter(article => {
          return article.is_published === true && article.status !== 'draft';
        });

        // Transform data artikel ke format yang dibutuhkan UI
        const formattedData = publishedArticles.map((article, index) => {
          // Tentukan badge berdasarkan kategori atau is_featured
          let badge = article.category || 'Berita';
          let badgeColor = 'bg-blue-100 text-blue-600'; // Default untuk berita
          
          if (article.is_featured) {
            badge = 'Featured';
            badgeColor = 'bg-yellow-100 text-yellow-600';
          } else if (article.category) {
            // Tentukan warna berdasarkan kategori
            const categoryLower = article.category.toLowerCase();
            if (categoryLower.includes('promo') || categoryLower.includes('diskon')) {
              badgeColor = 'bg-red-100 text-red-600';
            } else if (categoryLower.includes('investasi')) {
              badgeColor = 'bg-blue-100 text-blue-600';
            } else if (categoryLower.includes('tabungan')) {
              badgeColor = 'bg-green-100 text-green-600';
            } else if (categoryLower.includes('program')) {
              badgeColor = 'bg-purple-100 text-purple-600';
            }
          }

          // Format tanggal
          let formattedDate = '';
          if (article.created_at) {
            const date = new Date(article.created_at);
            formattedDate = date.toLocaleDateString('id-ID', {
              day: 'numeric',
              month: 'long',
              year: 'numeric'
            });
          }

          return {
            id: article.id || article.artikel_id || index,
            image: article.featured_image || '/sapi1.jpg',
            badge: badge,
            badgeColor: badgeColor,
            title: article.title || 'Judul Artikel',
            description: article.excerpt || article.content?.substring(0, 100) + '...' || 'Deskripsi artikel',
            date: formattedDate,
            category: article.category || 'Berita',
            content: article.content || article.excerpt || 'Konten artikel tidak tersedia',
            slug: article.slug,
            validUntil: null,
            is_featured: article.is_featured || false
          };
        });

        setPromoData(formattedData);
      } catch (error) {
        console.error('Error fetching articles:', error);
        setError('Gagal memuat artikel. Silakan coba lagi.');
        // Fallback ke data kosong jika API error
        setPromoData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchArticles();
  }, []);

  // Jika sedang menampilkan detail promo atau ada initialSelectedPromo
  if ((showDetailPromo && selectedPromo) || initialSelectedPromo) {
    return (
      <DetailPromo 
        onBack={() => {
          if (fromMemberArea) {
            // Jika dari member area, kembali langsung ke member area
            onBack();
          } else {
            // Jika dari list promo, kembali ke list
            setShowDetailPromo(false);
            setSelectedPromo(null);
          }
        }}
        activeBottomTab={activeBottomTab}
        setActiveBottomTab={setActiveBottomTab}
        selectedPromo={selectedPromo || initialSelectedPromo}
        fromMemberArea={fromMemberArea}
      />
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile Wrapper */}
      <div className="max-w-lg mx-auto bg-white shadow-2xl min-h-screen">
        
        {/* Header */}
        <header className="px-4 pt-8 pb-4 bg-white border-b border-gray-100">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <button 
                onClick={onBack}
                className="p-2 rounded-full hover:bg-gray-100 transition-colors"
              >
                <i className="fa-solid fa-arrow-left text-primary text-xl"></i>
              </button>
              <div className="flex items-center space-x-2">
                <Logo size="small" showText={false} />
                <span className="font-bold text-primary text-lg">{'Promo & Berita'}</span>
              </div>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="pb-24 bg-gray-50">
          {/* Promo & Berita List */}
          <section className="px-4 pt-6">
            {loading ? (
              <div className="space-y-4">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="bg-white rounded-2xl shadow-lg overflow-hidden animate-pulse">
                    <div className="h-48 bg-gray-300"></div>
                    <div className="p-4">
                      <div className="h-4 bg-gray-200 rounded w-1/4 mb-2"></div>
                      <div className="h-6 bg-gray-200 rounded w-3/4 mb-2"></div>
                      <div className="h-4 bg-gray-200 rounded w-full mb-2"></div>
                      <div className="h-4 bg-gray-200 rounded w-5/6"></div>
                    </div>
                  </div>
                ))}
              </div>
            ) : error ? (
              <div className="bg-white rounded-2xl shadow-lg p-8 text-center">
                <i className="fa-solid fa-exclamation-triangle text-red-500 text-4xl mb-4"></i>
                <p className="text-gray-600 mb-4">{error}</p>
                <button
                  onClick={() => window.location.reload()}
                  className="px-6 py-2 bg-primary text-white rounded-lg hover:bg-secondary transition-colors"
                >
                  Coba Lagi
                </button>
              </div>
            ) : promoData.length === 0 ? (
              <div className="bg-white rounded-2xl shadow-lg p-8 text-center">
                <i className="fa-solid fa-newspaper text-gray-400 text-4xl mb-4"></i>
                <p className="text-gray-600">Belum ada artikel yang tersedia.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {promoData.map((item) => (
                <div key={item.id} className="bg-white rounded-2xl shadow-lg overflow-hidden">
                  <div className="relative">
                    {/* Image */}
                    <div className="relative h-48 overflow-hidden">
                      <img 
                        src={item.image} 
                        alt={item.title}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute top-4 left-4">
                        <span className={`text-xs ${item.badgeColor} font-semibold px-3 py-1 rounded-full`}>
                          {item.badge}
                        </span>
                      </div>
                      <div className="absolute top-4 right-4">
                        <span className="bg-black/50 text-white text-xs font-medium px-2 py-1 rounded">
                          {item.category}
                        </span>
                      </div>
                    </div>

                    {/* Content */}
                    <div 
                      className="p-4 cursor-pointer"
                      onClick={() => {
                        setSelectedPromo(item);
                        setShowDetailPromo(true);
                      }}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-gray-500 text-xs">{item.date}</span>
                        {item.validUntil && (
                          <span className="text-orange-500 text-xs font-medium">
                            Berlaku hingga {item.validUntil}
                          </span>
                        )}
                      </div>
                      
                      <h3 className="text-primary font-bold text-lg mb-2">{item.title}</h3>
                      <p className="text-gray-600 text-sm mb-3">{item.description}</p>
                      <p className="text-gray-700 text-sm leading-relaxed">{item.content}</p>
                    </div>

                    {/* Action Buttons */}
                    <div className="px-4 pb-4">
                      {item.category === 'Promo' ? (
                        <button 
                          className="w-full bg-accent text-primary font-semibold py-3 px-4 rounded-xl hover:bg-accent/90 transition-colors"
                          onClick={() => {
                            setSelectedPromo(item);
                            setShowDetailPromo(true);
                          }}
                        >
                          <i className="fa-solid fa-tag mr-2"></i>
                          Ambil Promo
                        </button>
                      ) : item.category === 'Program' ? (
                        <button 
                          className="w-full bg-secondary text-white font-semibold py-3 px-4 rounded-xl hover:bg-secondary/90 transition-colors"
                          onClick={() => {
                            setSelectedPromo(item);
                            setShowDetailPromo(true);
                          }}
                        >
                          <i className="fa-solid fa-piggy-bank mr-2"></i>
                          Daftar Sekarang
                        </button>
                      ) : (
                        <button 
                          className="w-full bg-primary text-white font-semibold py-3 px-4 rounded-xl hover:bg-primary/90 transition-colors"
                          onClick={() => {
                            setSelectedPromo(item);
                            setShowDetailPromo(true);
                          }}
                        >
                          <i className="fa-solid fa-info-circle mr-2"></i>
                          Lihat Detail
                        </button>
                      )}
                    </div>
                  </div>
                </div>
                ))}
              </div>
            )}
          </section>
        </main>

        {/* Bottom Navigation */}
        <footer className="fixed bottom-0 left-0 right-0 max-w-lg mx-auto bg-white pt-2">
          <div className="bg-primary mx-4 mb-4 rounded-2xl shadow-lg">
            <div className="flex justify-around items-center h-20 text-white">
              <span 
                className={`text-center flex flex-col items-center cursor-pointer ${activeBottomTab === 'home' ? 'text-accent' : 'opacity-70 hover:opacity-100'} transition`}
                onClick={() => {
                  setActiveBottomTab('home');
                  onBack();
                }}
              >
                <i className="fa-solid fa-house text-2xl"></i>
                <span className="text-xs font-semibold mt-1">Beranda</span>
              </span>
              <span 
                className={`text-center flex flex-col items-center cursor-pointer ${activeBottomTab === 'savings' ? 'text-accent' : 'opacity-70 hover:opacity-100'} transition`}
                onClick={() => {
                  setActiveBottomTab('savings');
                  onBack();
                }}
              >
                <i className="fa-solid fa-piggy-bank text-2xl"></i>
                <span className="text-xs font-semibold mt-1">Tabungan</span>
              </span>
              <span 
                className={`text-center flex flex-col items-center cursor-pointer ${activeBottomTab === 'products' ? 'text-accent' : 'opacity-70 hover:opacity-100'} transition`}
                onClick={() => setActiveBottomTab('products')}
              >
                <i className="fa-solid fa-box-archive text-2xl"></i>
                <span className="text-xs font-semibold mt-1">Produk</span>
              </span>
              <span 
                className={`text-center flex flex-col items-center cursor-pointer ${activeBottomTab === 'investment' ? 'text-accent' : 'opacity-70 hover:opacity-100'} transition`}
                onClick={() => {
                  setActiveBottomTab('investment');
                  onBack();
                }}
              >
                <i className="fa-solid fa-arrow-trend-up text-2xl"></i>
                <span className="text-xs font-semibold mt-1">Investasi</span>
              </span>
              <span 
                className={`text-center flex flex-col items-center cursor-pointer ${activeBottomTab === 'account' ? 'text-accent' : 'opacity-70 hover:opacity-100'} transition`}
                onClick={() => {
                  setActiveBottomTab('account');
                  onBack();
                }}
              >
                <i className="fa-solid fa-user-circle text-2xl"></i>
                <span className="text-xs font-semibold mt-1">Akun</span>
              </span>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default PromoBerita;
